import type { StreamsPayload } from '@painpal/contracts';
import { formatPace, gradeAdjustedSpeeds, ZONE_LABELS, ZONE_NAMES } from '@painpal/core';
import { itemAt } from '../../lib/zones';
import { SPORT_COLORS, ZONE_PALETTES } from '../../theme/tokens';
import type { Variant } from '../../ui/useMediaQuery';

export interface KmSplit { km: number; paceS: number; gapS: number }
export interface SplitBar { km: number; pace: string; gap: string; heightPx: number; color: string; zone: string | null; label: string }

// The same distance rule as the grade: steps over 5 s are pauses and stopped samples cover no ground.
const MAX_STEP_S = 5;
const KM = 1000;
const MIN_BAR_PX = 24;
const LABEL_PX = 28;

// Whole kilometres only. The step that crosses a kilometre is split, so its overshoot starts the next one.
export function kmSplits(streams: StreamsPayload): KmSplit[] {
  const { t, speed } = streams;
  if (speed === undefined) return [];
  const adjusted = gradeAdjustedSpeeds(streams);
  const splits: KmSplit[] = [];
  let metres = 0;
  let gapMetres = 0;
  let seconds = 0;
  let previous: number | null = null;
  for (const [i, time] of t.entries()) {
    const dt = previous === null ? 0 : time - previous;
    previous = time;
    const v = speed[i];
    const g = adjusted[i];
    if (dt <= 0 || dt > MAX_STEP_S || typeof v !== 'number' || v <= 0 || typeof g !== 'number') continue;
    metres += v * dt;
    gapMetres += g * dt;
    seconds += dt;
    if (metres < KM) continue;
    const over = (metres - KM) / v;
    const kmSeconds = seconds - over;
    splits.push({ km: splits.length + 1, paceS: Math.round(kmSeconds), gapS: Math.round((kmSeconds * KM) / (gapMetres - g * over)) });
    metres -= KM;
    gapMetres = g * over;
    seconds = over;
  }
  return splits;
}

// Bounds descend in s/km; a pace at or under a bound is in the faster zone.
export const splitZone = (paceS: number, bounds: readonly number[]): number => bounds.filter((b) => paceS <= b).length;

function zoneName(zone: number): string {
  return `${itemAt(ZONE_LABELS.pace, zone)} ${itemAt(ZONE_NAMES.pace, zone).toUpperCase()}`;
}

// Taller is faster: the slowest km gets the minimum, the fastest fills the track under its label.
export function splitBars(splits: readonly KmSplit[], bounds: readonly number[] | null, trackPx: number): SplitBar[] {
  const paces = splits.map((s) => s.paceS);
  const slowest = Math.max(...paces);
  const span = slowest - Math.min(...paces);
  return splits.map((s) => {
    const zone = bounds === null ? null : splitZone(s.paceS, bounds);
    const name = zone === null ? null : zoneName(zone);
    const pace = formatPace(s.paceS);
    const gap = formatPace(s.gapS);
    const share = span === 0 ? 1 : (slowest - s.paceS) / span;
    return {
      km: s.km, pace, gap, zone: name,
      heightPx: Math.round(MIN_BAR_PX + (trackPx - MIN_BAR_PX - LABEL_PX) * share),
      color: zone === null ? SPORT_COLORS.run : itemAt(ZONE_PALETTES.pace, zone),
      label: [`KM ${s.km}`, `${pace} /KM`, `GAP ${gap} /KM`, ...(name === null ? [] : [name])].join(' · '),
    };
  });
}

export function fastestLabel(splits: readonly KmSplit[], withGap: boolean): string {
  const fastest = splits.reduce((best, s) => (s.paceS < best.paceS ? s : best));
  const gap = withGap ? ` (GAP ${formatPace(fastest.gapS)})` : '';
  return `FASTEST KM ${fastest.km} · ${formatPace(fastest.paceS)}${gap}`;
}

// A km column fits its pace above the bar and "GAP 4:10" (desktop) or a 44px tap target (phone) below it,
// so a long run scrolls sideways instead of overlapping its labels.
const SPLIT_COLUMN: Record<Variant, { min: number; gap: number }> = { desktop: { min: 52, gap: 10 }, phone: { min: 44, gap: 4 } };

export function splitsWidthPx(count: number, variant: Variant): number {
  const { min, gap } = SPLIT_COLUMN[variant];
  return count * min + (count - 1) * gap;
}
